import {
  ButtonHeadless,
  InputLabel,
  Spin,
  TextInput,
} from '@wajeez-web/components/ui';
import { useEffect, useState } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SwitchInput } from '@wajeez-web/components/ui';
import {
  useContentDispatch,
  CollectionComponetsActions,
  ContentEntityTypeActions,
  useContentSelector,
  FlowsActions,
} from '@wajeez-web/stores/content';
import { AiFillCloseCircle } from 'react-icons/ai';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { useParams, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { useQuery } from '@wajeez-web/components/helpers';

import { FlowCollectionComponentsModal } from '../components/FlowCollectionComponentsModal';
import { FlowComponentsModal } from '../components/FlowComponentsModal';
import { FlowNavigationsForm } from '../components/FlowNavigationsForm';
import { BiLoaderAlt } from 'react-icons/bi';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { FormAutocompleteV2 } from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';

type Section = { value: string; label: string };

type ContentEntityTypeInput = {
  id: string | number | undefined;
  value: {
    id: string | null | undefined;
    text: string | undefined;
  };
  label: string | undefined;
  isRequired: boolean;
  isError: boolean;
  isB2B?: boolean;
};

interface GeneralInput {
  name: string;
  paymentMethod: string;
}

const paymentMethods: { value: Server.PaymentMethod2; label: string }[] = [
  {
    value: Server.PaymentMethod2.Native,
    label: 'Native',
  },
  {
    value: Server.PaymentMethod2.Stripe,
    label: 'Stripe',
  },
  {
    value: Server.PaymentMethod2.Mixed,
    label: 'Mixed',
  },
];

const sections: Section[] = [
  { value: 'general', label: 'General' },
  { value: 'navigations', label: 'Navigations' },
];

const initialPageInputs: ContentEntityTypeInput[] = [
  {
    id: 'homePageId',
    value: {
      id: null,
      text: '',
    },
    label: 'Home',
    isB2B: false,
    isRequired: true,
    isError: true,
  },
  {
    id: 'b2BHomePageId',
    value: {
      id: null,
      text: '',
    },
    label: 'B2B Home',
    isB2B: true,
    isRequired: true,
    isError: true,
  },
  {
    id: 'b2BSkillsPageId',
    value: {
      id: null,
      text: '',
    },
    label: 'B2B Skills',
    isB2B: true,
    isRequired: false,
    isError: true,
  },
  {
    id: 'b2BLibraryPageId',
    value: {
      id: null,
      text: '',
    },
    label: 'B2B Library',
    isB2B: true,
    isRequired: false,
    isError: true,
  },
  {
    id: 'explorePageId',
    value: {
      id: null,
      text: '',
    },
    label: 'Explore',
    isB2B: false,
    isRequired: false,
    isError: true,
  },
  {
    id: 'wajeezCastPageId',
    value: {
      id: null,
      text: '',
    },
    label: 'Wajeez Cast',
    isB2B: false,
    isRequired: false,
    isError: true,
  },
  {
    id: 'searchAllPageId',
    value: {
      id: null,
      text: '',
    },
    label: 'Search All Page',
    isB2B: false,
    isRequired: false,
    isError: true,
  },
  {
    id: 'searchAllComponentId',
    value: {
      id: null,
      text: '',
    },
    label: 'Search All Component',
    isB2B: false,
    isRequired: false,
    isError: true,
  },
];

export const CreateAndEditFlowPage = () => {
  const { id } = useParams<{ id: string }>();

  const query = useQuery();

  const navigate = useNavigate();

  const contentDispatch = useContentDispatch();

  const [selectedSection, setSelectedSection] = useState<Section>(
    query.get('section')
      ? query.get('section') === 'navigations' && id
        ? { value: 'navigations', label: 'Navigations' }
        : { value: 'general', label: 'General' }
      : {
          value: 'general',
          label: 'General',
        }
  );
  const [isB2B, setIsB2B] = useState(true);
  const [filteredPageInputs, setFilteredPageInputs] =
    useState<ContentEntityTypeInput[]>(initialPageInputs);
  const [unfilteredPageInputs, setUnfilteredPageInputs] =
    useState<ContentEntityTypeInput[]>(initialPageInputs);
  const [selectedPageInput, setSelectedPageInput] =
    useState<ContentEntityTypeInput | null>(null);
  const [
    isFlowCollectionComponentsModalOpen,
    setIsFlowCollectionComponentsModalOpen,
  ] = useState(false);
  const [isFlowComponentsModalOpen, setIsFlowComponentsModalOpen] =
    useState(false);
  const [validatePageInputs, setValidatePageInputs] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isCreateFlowSuccess, setIsCreateFlowSuccess] = useState(false);

  const flowDetails = useContentSelector(({ flows }) => flows.flowDetailsById);
  const flowDetailsData = flowDetails.data;

  const contentEntityTypeLookupsStatus = useContentSelector(
    ({ contentEntity }) => contentEntity.status
  );

  const contentEntityTypeNavigationsStatus = useContentSelector(
    ({ contentEntityType }) => contentEntityType.navigations.status
  );

  const createFlowData = useContentSelector(
    ({ flows }) => flows.createFlow.data
  );

  const createFlowStatus = useContentSelector(
    ({ flows }) => flows.createFlow.status
  );
  const editFlowStatus = useContentSelector(
    ({ flows }) => flows.editFlow.status
  );

  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<GeneralInput>({ defaultValues: { paymentMethod: '' } });

  const onSubmit: SubmitHandler<GeneralInput> = async (data) => {
    for (const filteredPageInput of filteredPageInputs) {
      if (filteredPageInput.isError) {
        setValidatePageInputs(true);

        return;
      }
    }

    const otherData = _.reduce(
      filteredPageInputs,
      (obj: { [key: string]: string | null | undefined }, curr) => {
        obj[curr.id!] = curr.value.id;

        return obj;
      },
      {}
    );

    const formData = {
      flowName: data.name,
      paymentMethod: Number(data.paymentMethod),
    };

    if (!id) {
      const res = await contentDispatch(
        FlowsActions.createFlowAsync({
          ...formData,
          ...otherData,
        })
      );

      if (res.meta.requestStatus === 'fulfilled') {
        setIsCreateFlowSuccess(true);
      }
    } else {
      contentDispatch(
        FlowsActions.editFlowAsync({
          id: parseInt(id),
          ...formData,
          ...otherData,
        })
      );
    }
  };

  useEffect(() => {
    if (isCreateFlowSuccess && createFlowData) {
      navigate(`/flows/${createFlowData}/edit?section=navigations`);

      setSelectedSection({ value: 'navigations', label: 'Navigations' });
    }
  }, [isCreateFlowSuccess, createFlowData]);

  useEffect(() => {
    if (id) {
      contentDispatch(FlowsActions.getFlowDetailsByIdAsync(parseInt(id)));
    }
  }, [id]);

  useEffect(() => {
    if (id && flowDetailsData) {
      setUnfilteredPageInputs(
        unfilteredPageInputs.map((uf) => {
          const pageNameProp = uf?.id?.toString()?.split('Id')[0] + 'Name';

          return {
            ...uf,
            value: {
              id: flowDetailsData[
                uf?.id?.toString() as keyof typeof flowDetailsData
              ] as string,
              text: flowDetailsData[
                pageNameProp as keyof typeof flowDetailsData
              ] as string,
            },
          };
        })
      );

      flowDetailsData.isB2B !== undefined && setIsB2B(flowDetailsData.isB2B);

      reset({
        name: flowDetailsData.flowName,
        paymentMethod: flowDetailsData.paymentMethod?.toString(),
      });
    }
  }, [id, flowDetailsData]);

  useEffect(() => {
    if (errors.name) {
      setValidatePageInputs(true);
    }
  }, [errors.name]);

  useEffect(() => {
    contentDispatch(
      CollectionComponetsActions.getCollectionComponentLookupsAsync({})
    );
  }, []);

  useEffect(() => {
    contentDispatch(
      ContentEntityTypeActions.getContentEntityTypeLookupsAsync()
    );

    contentDispatch(
      ContentEntityTypeActions.getContentEntityTypeNavigationsAsync()
    );
  }, []);

  useEffect(() => {
    const unfilteredPageInputsWithErrorHandeled = unfilteredPageInputs.map(
      (p) => {
        if (p.isRequired && p.value.id) {
          return {
            ...p,
            isError: false,
          };
        } else if (!p.isRequired) {
          return {
            ...p,
            isError: false,
          };
        } else {
          return p;
        }
      }
    );

    if (isB2B) {
      setFilteredPageInputs(unfilteredPageInputsWithErrorHandeled);
    } else {
      setFilteredPageInputs(
        unfilteredPageInputsWithErrorHandeled.filter((p) => !p.isB2B)
      );
    }
  }, [unfilteredPageInputs, isB2B]);

  useEffect(() => {
    window.history.replaceState(
      null,
      '',
      `/flows/${id || 'new'}${id ? '/edit' : ''}?section=${
        selectedSection.value
      }`
    );
  }, [selectedSection, id, createFlowData]);

  const renderSections = () => {
    return sections.map((s) => {
      return (
        <div key={s.value}>
          <div
            className={`px-2 py-2 text-[14px] font-medium hover:bg-warmGray-100 hover:text-primary rounded-[4px] transition-colors cursor-pointer ${
              selectedSection.value === s.value
                ? 'bg-warmGray-100 text-primary'
                : 'text-warmGray-500'
            }`}
            onClick={() => {
              if (s.value === 'navigations' && !(createFlowData || id)) {
                setIsConfirmationModalOpen(true);

                return;
              }

              setSelectedSection(s);
            }}
          >
            {s.label}
          </div>
        </div>
      );
    });
  };

  const renderPageInputs = () => {
    return filteredPageInputs.map((p) => {
      return (
        <div key={p.id} className="relative w-full">
          <InputLabel
            text={
              t(`inputs.labels.${p.label}`, p.label?.toString() || '') as string
            }
            name={p.id as string}
            required={p.isRequired}
          />
          <div className="">
            {!p.value.id ? (
              <div className="w-[65px]">
                <ButtonHeadless
                  paddingYStyle="py-1"
                  action={() => {
                    setSelectedPageInput(p);

                    if (p.id !== 'searchAllComponentId') {
                      setIsFlowCollectionComponentsModalOpen(true);
                    } else {
                      setIsFlowComponentsModalOpen(true);
                    }
                  }}
                >
                  <div className="text-white">
                    <ReturnTranslatedText
                      origintext="Choose"
                      text={`buttons.${'Choose'}`}
                    />
                  </div>
                </ButtonHeadless>
              </div>
            ) : (
              <div className="w-full flex justify-start items-center">
                <div className="w-full max-w-[200px] py-4 px-3 rounded-md border text-center break-words">
                  {p.value.text}
                </div>
                <div
                  className="ms-2 text-warmGray-400 cursor-pointer hover:text-warmGray-300 active:text-warmGray-400"
                  onClick={() => {
                    setUnfilteredPageInputs(
                      unfilteredPageInputs.map((uf) => {
                        if (uf.id === p.id) {
                          return {
                            ...uf,
                            value: {
                              id: '',
                              text: '',
                            },
                            isError: true,
                          };
                        } else {
                          return uf;
                        }
                      })
                    );
                  }}
                >
                  <AiFillCloseCircle />
                </div>
              </div>
            )}
          </div>
          {validatePageInputs && p.isError && (
            <div className="absolute top-[100%] right-0 text-[13px] text-red-500">
              <ReturnTranslatedText
                origintext="This field is required"
                text={`general.${'This field is required'}`}
              />
            </div>
          )}
        </div>
      );
    });
  };

  const renderSectionContent = () => {
    switch (selectedSection.value) {
      case 'general':
        return (
          <div className="w-full flex flex-col items-start justify-start">
            <div>
              <SwitchInput
                name="isB2B"
                onChange={(name, value) => {
                  setIsB2B(value);
                }}
                label="B2B"
                value={isB2B}
              />
            </div>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mt-5 w-full max-w-[300px]"
              noValidate
            >
              <div className="relative w-full">
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label="Name"
                      required
                      onChange={(name, value, event) => {
                        field.onChange(event);
                      }}
                      isControlled={true}
                    />
                  )}
                />
                {errors.name && (
                  <div className="absolute top-[100%] right-0 text-[13px] text-red-500">
                    <ReturnTranslatedText
                      origintext="This field is required"
                      text={`general.${'This field is required'}`}
                    />
                  </div>
                )}
              </div>
              <div className="mt-3 w-full">
                <FormAutocompleteV2
                  name="paymentMethod"
                  control={control}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {
                      selectOptions: paymentMethods.map((p) => {
                        return {
                          value: p.value.toString(),
                          label: p.label,
                        };
                      }),
                    },
                    label: 'Payment Method',
                    required: true,
                  }}
                  required
                />
              </div>
              <div className="mt-3 w-full flex flex-col space-y-3">
                {renderPageInputs()}
              </div>
              <div className="w-[70px] absolute top-6 rtl:left-6 ltr:right-6">
                <ButtonHeadless
                  paddingYStyle="py-2"
                  type="submit"
                  isDisabled={
                    createFlowStatus === 'loading' ||
                    editFlowStatus === 'loading'
                  }
                >
                  <div className="w-full flex justify-center items-center text-white">
                    <ReturnTranslatedText
                      origintext="Save"
                      text={`buttons.${'Save'}`}
                    />
                    {(createFlowStatus === 'loading' ||
                      editFlowStatus === 'loading') && (
                      <BiLoaderAlt className="mr-1 animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </form>
          </div>
        );
      case 'navigations':
        return <FlowNavigationsForm flowId={createFlowData} />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full h-inner-screen flex justify-start items-start">
      <div className="w-[250px] h-full px-4 py-6 border-e border-warmGray-200 overflow-x-hidden">
        <h1 className="w-full pb-2 border-b text-[22px] font-bold text-center">
          Create Flow
        </h1>
        <div className="mt-3">{renderSections()}</div>
      </div>
      <div className="relative w-full h-inner-screen py-6 px-6 overflow-y-auto overflow-x-hidden">
        {flowDetails.status === 'loading' ||
        contentEntityTypeLookupsStatus === 'loading' ||
        contentEntityTypeNavigationsStatus === 'loading' ? (
          <Spin />
        ) : (
          renderSectionContent()
        )}
      </div>
      {isFlowCollectionComponentsModalOpen && (
        <FlowCollectionComponentsModal
          isOpen={isFlowCollectionComponentsModalOpen}
          onClose={() => {
            setIsFlowCollectionComponentsModalOpen(false);
          }}
          onSubmit={(collectionComponent) => {
            const pageInputsNew = unfilteredPageInputs.map((p) => {
              if (selectedPageInput && p.id === selectedPageInput.id) {
                return {
                  ...selectedPageInput,
                  value: {
                    id: collectionComponent?.id,
                    text: collectionComponent?.referenceName,
                  },
                };
              } else {
                return p;
              }
            });

            setUnfilteredPageInputs(pageInputsNew);

            setIsFlowCollectionComponentsModalOpen(false);
          }}
        />
      )}
      {isFlowComponentsModalOpen && (
        <FlowComponentsModal
          isOpen={isFlowComponentsModalOpen}
          onClose={() => {
            setIsFlowComponentsModalOpen(false);
          }}
          onSubmit={(component) => {
            const pageInputsNew = unfilteredPageInputs.map((p) => {
              if (selectedPageInput && p.id === selectedPageInput.id) {
                return {
                  ...selectedPageInput,
                  value: {
                    id: component?.id,
                    text: component?.referenceName,
                  },
                };
              } else {
                return p;
              }
            });

            setUnfilteredPageInputs(pageInputsNew);

            setIsFlowComponentsModalOpen(false);
          }}
        />
      )}
      {isConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => {
            setIsConfirmationModalOpen(false);
          }}
          onSubmit={() => {
            setIsConfirmationModalOpen(false);
          }}
          message="You should create the flow to continue!"
        />
      )}
    </div>
  );
};
